<!-- Freeze Charges Section -->
<div class="mb-4" data-testid="freeze-charges-section">
  <div class="flex align-items-center justify-content-between mb-3">
    <h5
      class="text-base font-semibold text-900 m-0"
      data-testid="freeze-charges-label"
    >
      {{ 'POS.MEMBERSHIP_ADDON.BILLDETAILS' | translate }}
    </h5>
    @if (isLoading()) {
      <p-progressSpinner
        styleClass="w-1rem h-1rem"
        strokeWidth="4"
        data-testid="charges-loading-spinner"
      />
    }
  </div>

  <!-- Display Mode (No Discount Applied) -->
  @if (!applyDiscount()) {
    <div class="border-1 border-200 border-round text-sm surface-0 overflow-hidden">
      <!-- Charge Items with inline tax breakdown -->
      @for (charge of charges(); track charge; let i = $index) {
        <div
          class="px-3 pt-2 pb-1 servicechargecontainer"
          [attr.data-testid]="'charge-display-' + i"
        >
          <!-- Charge name + base amount -->
          <div class="flex align-items-center justify-content-between">
            <span class="text-sm font-medium text-900">{{ charge['name_primary_language'] }}</span>
            <span class="text-sm font-bold text-900">
              {{ getChargeNetCost(charge) | currencyformat }}
            </span>
          </div>
          <!-- Per-charge tax lines (only when not inclusive) -->
          @if (!isInclusive()) {
            @for (taxLine of getChargeTaxBreakdown(charge); track taxLine.type) {
              <div
                class="flex align-items-center justify-content-between mt-1"
                [attr.data-testid]="'charge-tax-' + i + '-' + taxLine.type"
              >
                <span class="text-xs text-500 ml-2">
                  <i class="fa fa-angle-right mr-1"></i>{{ taxLine.type }} ({{ taxLine.rate }}%)
                </span>
                <span class="text-xs text-500">
                  {{ taxLine.amt_in_minor_currency | currencyformat }}
                </span>
              </div>
            }
          }
        </div>
      }

      <!-- Total Taxes summary row (if not inclusive) -->
      @if (!isInclusive() && getTotalChargeTaxAmount() > 0) {
        <div class="px-3 py-2 flex align-items-center justify-content-between servicechargecontainer border-top-1 border-200" data-testid="total-taxes-row">
          <span class="text-sm font-medium text-600">{{ 'POS.MEMBERSHIP_ADDON.TAXES' | translate }}</span>
          <span class="text-sm font-bold text-900">
            {{ getTotalChargeTaxAmount() | currencyformat }}
          </span>
        </div>
      }

      <!-- Apply Discount Button -->
      @if (!discountApplied()) {
        <div class="px-3 py-2">
          <p-button
            (click)="onApplyDiscountToggle()"
            label="{{ 'POS.MEMBERSHIP_ADDON.APPLY_DISCOUNT' | translate }}"
            icon="fa fa-percentage"
            styleClass="p-button-sm p-button-outlined w-full"
            [attr.aria-label]="'apply-discount-button'"
            [attr.data-testid]="'apply-discount-button'"
          />
        </div>
      }

      <!-- Edit/Remove Discount Buttons -->
      @if (discountApplied()) {
        <div class="px-3 py-2 flex gap-2">
          <p-button
            (click)="onApplyDiscountToggle()"
            label="{{ 'POS.MEMBERSHIP_ADDON.EDIT_DISCOUNT' | translate }}"
            icon="fa fa-edit"
            styleClass="p-button-sm p-button-outlined flex-1"
            [attr.aria-label]="'edit-discount-button'"
            [attr.data-testid]="'edit-discount-button'"
          />
          <p-button
            (click)="onRemoveDiscount()"
            icon="fa fa-times"
            label="{{ 'POS.MEMBERSHIP_ADDON.REMOVE_DISCOUNT' | translate }}"
            styleClass="text-xs p-button-text -mr-3"
            [attr.aria-label]="'remove-discount-button'"
            [attr.data-testid]="'remove-discount-button'"
          />
        </div>
      }

      <!-- Grand Total -->
      <div class="px-3 py-2 flex align-items-center justify-content-between bg-primary text-white">
        <span class="font-medium">{{ 'POS.MEMBERSHIP_ADDON.GRAND_TOTAL' | translate }}</span>
        <span class="text-lg font-bold">
          {{ getTotalOneTimeCost() | currencyformat }}
        </span>
      </div>
    </div>
  }

  <!-- Discount Edit Mode -->
  @if (applyDiscount()) {
    <div>
      <div class="border-1 border-300 border-round text-sm surface-0">
        <!-- Header -->
        <div class="px-3 py-2 flex align-items-center justify-content-between">
          <strong class="flex align-items-center">
            {{ 'POS.MEMBERSHIP_ADDON.ONE_TIME_DISCOUNT' | translate }}
          </strong>
        </div>

        <!-- Charge Discount Items -->
        @for (charge of charges(); track charge; let i = $index) {
          <div class="servicechargecontainer_edit">
            <div
              class="px-3 py-2 flex align-items-center justify-content-between"
              [attr.data-testid]="'charge-discount-' + i"
            >
              <span class="text-sm font-medium text-900">{{ charge['name_primary_language'] }}</span>
              <div class="flex align-items-center gap-2">
                <span class="text-sm font-bold text-900">{{ charge['original_amount'] | currencyformat }}</span>
                <p-inputNumber
                  [formControl]="$any(getFormControl(i))"
                  [minFractionDigits]="2"
                  [maxFractionDigits]="4"
                  styleClass="w-4rem h-2rem text-sm"
                  inputStyleClass="px-1"
                  (onBlur)="onChargeAmountBlur(i)"
                  [attr.aria-label]="'charge-amount-input-' + i"
                  [attr.data-testid]="'charge-amount-input-' + i"
                />
                <div class="p-inputgroup w-4rem">
                  <span class="p-inputgroup-addon">%</span>
                  <p-inputNumber
                    [formControl]="$any(getDiscountControl(i))"
                    [minFractionDigits]="2"
                    [maxFractionDigits]="4"
                    mode="decimal"
                    styleClass="w-3rem -ml-1 px-0 text-sm"
                    inputStyleClass="px-1"
                    (onBlur)="onChargeDiscountBlur(i)"
                    [attr.aria-label]="'charge-discount-input-' + i"
                    [attr.data-testid]="'charge-discount-input-' + i"
                  />
                </div>
              </div>
            </div>
          </div>
        }

        <!-- Invalid Discount Error -->
        @if (inValidDiscount()) {
          <div class="px-3 flex justify-content-end" data-testid="invalid-discount-error">
            <p-message
              severity="error"
              icon="fa fa-exclamation-triangle"
              styleClass="-mt-3 -mb-2"
            >
              {{ 'POS.MEMBERSHIP_ADDON.YOUCANNOTAPPLY' | translate }}
              {{ singleStaffDiscount() }} %
              {{ 'POS.MEMBERSHIP_ADDON.DISCOUNT' | translate }}
            </p-message>
          </div>
        }
      </div>

      <!-- Action Buttons -->
      <div class="flex align-items-center justify-content-between mt-3">
        <button
          (click)="onCancelDiscount()"
          pButton
          type="button"
          label="{{ 'common.PLACEHOLDER.CANCEL' | translate }}"
          class="text-sm p-button-text"
          [attr.aria-label]="'cancel-discount-button'"
          [attr.data-testid]="'cancel-discount-button'"
        ></button>
        <button
          (click)="onApplyDiscountToggle()"
          pButton
          type="button"
          label="{{ 'common.PLACEHOLDER.APPLY' | translate }}"
          class="text-sm p-button-text"
          [attr.aria-label]="'apply-discount-confirm-button'"
          [attr.data-testid]="'apply-discount-confirm-button'"
        ></button>
      </div>
    </div>
  }
</div>
